// ─── RULES ENGINE ─────────────────────────────────────────────────────────────
// Three-layer evaluation: Care Partner profiles → coverage assumptions → gap detection

// notation helpers (inlined for browser bundle)
const FOH_HEADER = /-- Managed by \S+ --/;

function parseFOHBlock(desc) {
  if (!desc || !FOH_HEADER.test(desc)) return null;
  const assigneeMatch = desc.match(/assignee:\s*(\S+)/i);
  const reviewedMatch = desc.match(/reviewed:\s*(yes|no)/i);
  // "reminder:" is the current wire key; "exempt:" is the legacy key still
  // present on events written before the Grove rename — never rewritten in
  // bulk, so the parser has to keep reading both indefinitely.
  const reminderMatch = desc.match(/reminder:\s*(yes|no)/i) || desc.match(/exempt:\s*(yes|no)/i);
  // Links this event/instance back to a families/{id}/sessions/{id} doc —
  // only present on caregiver-booked events (see ScheduleSitter).
  const sessionMatch = desc.match(/session:\s*(\S+)/i);
  return {
    assignee: assigneeMatch?.[1] && assigneeMatch[1] !== 'none' ? assigneeMatch[1] : null,
    reviewed: reviewedMatch?.[1] === 'yes',
    reminder: reminderMatch?.[1] === 'yes',
    session: sessionMatch?.[1] || null,
  };
}
export function parseFOHMeta(desc) { return parseFOHBlock(desc); }

// ─── LAYER 1: RESOLVE WORK MODES ──────────────────────────────────────────────

/**
 * Given a Care Partner's events for a day and their workModeProfiles,
 * returns the active work mode (or null if available).
 */
function resolveWorkMode(events, workModeProfiles = []) {
  for (const event of events) {
    const title = (event.summary || '').toLowerCase();
    for (const profile of workModeProfiles) {
      for (const kw of profile.keywords || []) {
        if (title.includes(kw.toLowerCase())) {
          return profile;
        }
      }
    }
  }
  return null;
}

/**
 * For Care Partners with "standing window" convention (never scheduled),
 * returns their default work mode for weekdays.
 */
function getStandingWorkMode(carePartner, isoDate) {
  const date = new Date(isoDate);
  const dow = date.getUTCDay(); // 0=Sun, 6=Sat
  if (dow === 0 || dow === 6) return null; // weekend = available

  return carePartner.standingWorkMode || null; // configured in their profile
}

// ─── LAYER 2: COVERAGE ASSUMPTIONS ───────────────────────────────────────────

/**
 * Determines coverage state for a given day across all Care Partners.
 * Returns { carePartnerStates, bothUnavailable, backupCarePresent }
 */
function resolveDayCoverage(dayData, carePartners, appConfig) {
  const { events = [], isoDate } = dayData;
  const registries = appConfig?.keywordRegistries || {};
  const auxKeywords = (registries.auxiliaryCare || []).map(k => k.toLowerCase());

  // Check for backup care
  const backupCarePresent = events.some(e =>
    auxKeywords.some(kw => (e.summary || '').toLowerCase().includes(kw))
  );

  const carePartnerStates = carePartners.map(cp => {
    const cpEvents = events.filter(e => e._calendarId === cp.calendarId);

    // Resolve work mode — convention-based first, then event-based
    let workMode = null;
    if (cp.scheduleConvention === 'standing_window') {
      workMode = getStandingWorkMode(cp, isoDate);
    } else if (cp.scheduleConvention === 'always_scheduled' || cp.scheduleConvention === 'mixed') {
      workMode = resolveWorkMode(cpEvents, cp.workModeProfiles || []);

      // Unknown all-day event with no keyword match — flag for review
      const unknownAllDay = cpEvents.filter(e => e.start?.date && !workMode &&
        !isReminder(e) && !isKidEvent(e, carePartners)
      );
      if (unknownAllDay.length > 0 && cp.scheduleConvention === 'always_scheduled') {
        workMode = { name: 'Unknown', coverageAssumption: 'flag_all', _unknown: true, _events: unknownAllDay };
      }
    }

    return {
      carePartner: cp,
      workMode,
      events: cpEvents,
      isAvailable: !workMode,
    };
  });

  const unavailableCount = carePartnerStates.filter(s => !s.isAvailable).length;
  const bothUnavailable = carePartners.length >= 2 && unavailableCount >= carePartners.length;

  return { carePartnerStates, bothUnavailable, backupCarePresent };
}

// ─── LAYER 3: GAP DETECTION ───────────────────────────────────────────────────

/**
 * Main entry point. Evaluates a single day and returns an array of alerts.
 * @param {object} dayData - { isoDate, events[] }
 * @param {object[]} carePartners - array of CarePartner profiles
 * @param {object} appConfig - AppConfig from Firestore
 * @returns {Alert[]}
 */
function evaluateDay(dayData, carePartners, appConfig) {
  const alerts = [];
  const { events = [], isoDate } = dayData;
  const registries = appConfig?.keywordRegistries || {};

  const { carePartnerStates, bothUnavailable, backupCarePresent } = resolveDayCoverage(
    dayData, carePartners, appConfig
  );

  // Backup care suppresses all gap alerts
  if (backupCarePresent) {
    return [{ level: 'info', rule: 'backup_care_active', message: 'Coverage confirmed via backup care', isoDate }];
  }

  const kidEvents = events.filter(e => isKidEvent(e, carePartners));
  const schoolSignals = detectSchoolSignals(events, registries);

  // ── Rule: Both Unavailable ────────────────────────────────────────────────
  if (bothUnavailable && kidEvents.length > 0) {
    alerts.push({
      level: 'high',
      rule: 'both_unavailable',
      message: `Both Care Partners unavailable — ${kidEvents.length} kid event${kidEvents.length > 1 ? 's' : ''} need coverage`,
      isoDate,
      events: kidEvents,
      carePartnerStates,
      canFlex: true, // Busy ≠ unavailable — always show assignee options
    });
  }

  // ── Rule: School Day No Coverage ─────────────────────────────────────────
  if (schoolSignals.isSchoolDay && !bothUnavailable) {
    const noOneCovered = carePartnerStates.every(s => !s.isAvailable);
    if (noOneCovered) {
      alerts.push({
        level: 'high',
        rule: 'school_day_no_coverage',
        message: 'School day — who handles pickup?',
        isoDate,
        canFlex: true,
      });
    }
  }

  // ── Rule: No School / Day Off ─────────────────────────────────────────────
  if (schoolSignals.isNoSchool) {
    const noCarePlanned = carePartnerStates.every(s => !s.isAvailable);
    if (noCarePlanned) {
      alerts.push({
        level: 'medium',
        rule: 'day_off_no_care',
        message: 'Kids home — no care scheduled',
        isoDate,
        canFlex: true,
      });
    }
  }

  // ── Rule: Work Day Kid Overlap ────────────────────────────────────────────
  for (const state of carePartnerStates) {
    if (!state.workMode || state.isAvailable) continue;

    const { unavailableStart, unavailableEnd, name: modeName, _unknown } = state.workMode;
    const conflictingKidEvents = kidEvents.filter(e => {
      if (!unavailableStart || !unavailableEnd) return _unknown; // unknown mode = flag all
      const eventTime = getEventTime(e);
      if (!eventTime) return false;
      return eventTime >= unavailableStart && eventTime <= unavailableEnd;
    });

    if (conflictingKidEvents.length > 0) {
      alerts.push({
        level: state.workMode._unknown ? 'medium' : 'medium',
        rule: 'work_day_kid_overlap',
        message: state.workMode._unknown
          ? `${state.carePartner.name} has an unrecognized calendar event — ${conflictingKidEvents.length} kid event(s) may need coverage`
          : `${state.carePartner.name} is in ${modeName} mode — ${conflictingKidEvents.length} kid event(s) during unavailable window`,
        isoDate,
        carePartner: state.carePartner,
        events: conflictingKidEvents,
        canFlex: true, // Care Partner can still be assigned
        conflictAck: `Noted — ${state.carePartner.name} has a conflict this window. Assignment saved.`,
      });
    }
  }

  return alerts;
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function isReminder(event) {
  const foh = parseFOHBlock(event.description);
  return foh?.reminder === true;
}

function isKidEvent(event, carePartners) {
  const cpCalIds = new Set(carePartners.map(cp => cp.calendarId));
  return !cpCalIds.has(event._calendarId);
}

function detectSchoolSignals(events, registries) {
  const noSchoolKws = (registries.schoolSignals || ['No School', 'Day Off', 'Holiday', 'Early Release', 'Half Day'])
    .map(k => k.toLowerCase());

  const titles = events.map(e => (e.summary || '').toLowerCase());
  const isNoSchool = titles.some(t => noSchoolKws.some(kw => t.includes(kw)));

  // Default: school day Mon–Fri unless no-school signal
  const dow = new Date().getUTCDay();
  const isWeekday = dow >= 1 && dow <= 5;

  return {
    isSchoolDay: isWeekday && !isNoSchool,
    isNoSchool,
  };
}

function getEventTime(event) {
  if (event.start?.dateTime) {
    return event.start.dateTime.split('T')[1]?.substring(0, 5) || null;
  }
  return null; // all-day event
}

/**
 * Evaluates a full window of days and returns a summary.
 * @param {object[]} days - array of { isoDate, events[] }
 * @param {object[]} carePartners
 * @param {object} appConfig
 * @returns {{ days: DayResult[], alertSummary: { high, medium, clear } }}
 */
function evaluateWindow(days, carePartners, appConfig) {
  const results = days.map(day => ({
    isoDate: day.isoDate,
    events: day.events,
    alerts: evaluateDay(day, carePartners, appConfig),
  }));

  const alertSummary = {
    high: results.filter(d => d.alerts.some(a => a.level === 'high')).length,
    medium: results.filter(d => d.alerts.some(a => a.level === 'medium')).length,
    clear: results.filter(d => d.alerts.length === 0 || d.alerts.every(a => a.level === 'info')).length,
  };

  return { days: results, alertSummary };
}

export { evaluateDay, evaluateWindow, resolveDayCoverage, resolveWorkMode };
